<template>
    <div class="rights-panel">
        <el-alert
            v-if="channel !== null && channel.status === 'FINISHED'"
            title="Rights cannot be managed on a finished channel"
            type="warning"
            effect="dark"
            :closable="false"
        ></el-alert>
        <div class="trigger-modal-button-container">
            <el-button
                type="primary"
                class="gcmcn-primary-btn"
                :disabled="channel.status === 'FINISHED'"
                @click="openRightsManagementModal"
            >Add right to group</el-button>
        </div>
        <gcmcn-filters
            ref="gcmcn-filters"
            :filter-values="[selectedSortType, searchFilter]"
            @reset-filters="resetFilters"
        >
            <dropdown
                name="Sort"
                :multiple="false"
                :list="$store.getters.sortTypeList"
                itemName="name"
                itemValue="value"
                :itemsSelected="selectedSortType.length > 0 ? [selectedSortType] : []"
                @on-select-item="selectedSortType = $event"
            ></dropdown>
            <search-filter @new-value="searchFilter = $event"></search-filter>
        </gcmcn-filters>
        <div class="channel-takers" :style="{height: `calc(100vh - ${this.usedHeight}px)`}">
            <template v-if="currentChannelAvailableTakers.length > 0">
                <taker
                    v-for="taker in currentChannelAvailableTakers"
                    :key="taker.uid"
                    :taker="taker"
                    :channel-confirmed="channel.confirmed"
                    :broadcast-right="channelBroadcastRights"
                    :channel-finished="channel.status === 'FINISHED'"
                    :filtered-blanking-profile="filteredBlankingProfile"
                    :channel-configurations="channelConfigurations"
                    @create-broadcast-right="createBroadcastRight"
                    @delete-broadcast-right="deleteBroadcastRight"
                    :channel="channel"
                ></taker>
            </template>
            <template v-else>
                <div v-if="searchFilter === ''" class="list-empty">No taker(s) have access to that channel</div>
                <div v-else class="list-empty">No taker(s) match this filters</div>
            </template>
        </div>
        <default-modal
            :show="showRightsModal"
            :closeOnClickModal="false"
            @close="closeRightsManagementModal"
        >
            <h2 class="rights-management-modal-title">
                Add right to the whole group
            </h2>
            <div class="gcmcn-form-input">
                <el-select
                    v-model="takerGroupSelected"
                    placeholder="Select a group"
                    popper-class="gcmcn-popper"
                    multiple
                    size="mini"
                >
                    <el-option
                        v-for="takerGroup in availableTakerGroupsForChannel"
                        :key="takerGroup.uid"
                        :label="takerGroup.name"
                        :value="takerGroup.uid">
                    </el-option>
                </el-select>
            </div>
            <div class="modal-action-button-container">
                <el-button type="text" small @click="closeRightsManagementModal">Cancel</el-button>
                <el-button
                    :disabled="takerGroupSelected.length === 0"
                    class="gcmcn-primary-btn small validate-btn"
                    @click="changeRights"
                >Validate</el-button>
            </div>
        </default-modal>
    </div>
</template>

<script>
import Taker from './taker.vue';
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import NsLoader from 'fundation-app-vuejs/src/components/partials/ns-vue-loader';
import LoadingTemplate from '../../partials/ns-loader-template.vue';
import DefaultModal from 'gcmcn-common/src/components/gcmcn-modal-theme.vue';
import { Dropdown, SearchFilter, GcmcnFilters } from 'gcmcn-common';
export default {

    components: {
        Taker,
        Dropdown,
        SearchFilter,
        GcmcnFilters,
        DefaultModal,
        NsLoader,
        LoadingTemplate,
    },

    props: {
        channel: {
            type: Object,
            required: true,
        },
        broadcastRight: {
            type: Array,
            required: true,
        },
        filteredBlankingProfile: {
            type: Array,
            required: true,
        },
        channelConfigurations: {
            type: Array,
            required: true,
        }
    },

    data (){
        return {
            searchFilter: '',
            selectedSortType: 'asc',
            showRightsModal: false,
            takerGroupSelected: [],
            usedHeight: 0,
        };
    },

    computed: {
        ...mapGetters([
            'takers',
            'takerGroups',
            'takerGroupsByUid',
            'windowWidth'
        ]),
        channelBroadcastRights(){
            return this.broadcastRight.filter(
                br => br.channel_uid === this.channel.uid
            );
        },
        availableTakerGroupsForChannel (){
            return this.takerGroups.filter((tg) => tg.scopes.entity_uid === this.channel.scopes.entity_uid);
        },
        currentChannelAvailableTakers(){
            let taker = this.takers.slice();
            if(this.channel.scopes && this.channel.scopes.entity_uid){
                taker = taker.filter(taker => {
                    return this.channel.scopes.entity_uid === taker.scopes.entity_uid;
                });
            }
            if(this.selectedSortType.length > 0){
                taker.sort((a,b) => {
                    return a.name.toLowerCase() === b.name.toLowerCase() ? 0 : a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
                });
                if(this.selectedSortType !== 'asc'){
                    taker.reverse();
                }
            }
            if(this.searchFilter !== ''){
                taker = taker.filter((ta) => ta.name.toLowerCase().includes(this.searchFilter.toLowerCase()));
            }
            return taker;
        },
        entityScope (){
            return { 'X-Entity-Uid': this.channel.scopes.entity_uid};
        },
    },
    watch: {
        windowWidth (){
            this.computeFilterHeight();
        },
    },
    mounted (){
        this.$nextTick(()=>{
            this.computeFilterHeight();
        });
    },

    methods: {
        ...mapActions([
            'createItem'
        ]),
        openRightsManagementModal() {
            this.showRightsModal = true;
            if (this.takerGroups.length === 1) {
                this.takerGroupSelected = [this.takerGroups[0].uid];
            }
        },
        closeRightsManagementModal() {
            this.showRightsModal = false;
            this.$set(this, 'takerGroupSelected', []);
        },
        changeRights() {
            if (this.takerGroupSelected.length > 0) {
                this.currentChannelAvailableTakers.forEach(taker => {
                    for(let takerGroup of this.takerGroupSelected){
                        let group = this.takerGroupsByUid(takerGroup);
                        if (group.takers_uid && group.takers_uid.includes(taker.uid)) {
                            let right = this.getBroadcastRightByTaker(taker.uid);
                            if (!(right && right.revoked === false)) {
                                this.createBroadcastRight(taker.uid);
                            }
                        }
                    }
                });
                this.closeRightsManagementModal();
            }
        },
        resetFilters (){
            this.searchFilter = '';
            this.selectedSortType = '';
        },
        getBroadcastRightByTaker(takerUid){
            return this.channelBroadcastRights.find(
                br => br.taker_uid === takerUid
            );
        },

        deleteBroadcastRight (takerUid){
            let existingBroadcastRight = this.getBroadcastRightByTaker(takerUid);
            if(existingBroadcastRight){
                this.$store.dispatch('patchItem', {
                    item: {
                        uid: existingBroadcastRight.uid,
                        revoked: true,
                        revocation_date: moment.utc().format(),
                        url: existingBroadcastRight.url
                    },
                    model: 'broadcastRight',
                }).then((res) => {
                    this.$message({
                        message: `Broadcast right revoked for channel ${this.channel.display_name} and taker ${this.$store.getters.takersByUid(res.taker_uid).name}`,
                        type: 'success',
                    });
                    this.$emit('on-update-broadcast-right');
                }).catch((e) => {
                    this.$message.error('Error on broadcast right revocation.');
                    console.error(e);
                });
            }
        },

        createBroadcastRight (takerUid){
            let existingBroadcastRight = this.getBroadcastRightByTaker(takerUid);
            if(existingBroadcastRight){
                this.$store.dispatch('patchItem', {
                    item: {
                        uid: existingBroadcastRight.uid,
                        revoked: false,
                        revocation_date: null,
                        url: existingBroadcastRight.url
                    },
                    model: 'broadcastRight',
                }).then((res) => {
                    this.$message({
                        message: `Broadcast right created for channel ${this.channel.display_name} and taker ${this.$store.getters.takersByUid(res.taker_uid).name}`,
                        type: 'success',
                    });
                    this.$emit('on-update-broadcast-right');
                }).catch((e) => {
                    this.$message.error('Error on broadcast right creation.');
                    console.error(e);
                });
            }
            else{
                this.$store.dispatch('createItem', {
                    item: {
                        channel_uid: this.channel.uid,
                        taker_uid: takerUid
                    },
                    model: 'broadcastRight',
                    url: this.$store.state.api.meta.listModels.broadcastRight.url,
                    scopes: {'X-Entity-Uid': this.channel.scopes.entity_uid },
                }).then((res) => {
                    this.$message({
                        message: `Broadcast right created for channel ${this.channel.display_name} and taker ${this.$store.getters.takersByUid(res.taker_uid).name}`,
                        type: 'success',
                    });
                    this.$emit('on-update-broadcast-right');
                }).catch((e) => {
                    this.$message.error('Error on broadcast right creation.');
                    console.error(e);
                });
            }

        },
        computeFilterHeight() {
            if(this.$refs['gcmcn-filters']){
                const clientRect = this.$refs['gcmcn-filters'].$el.getBoundingClientRect();
                this.usedHeight = clientRect.top + clientRect.height + 10 + 32; // +10 par ce que l'element contient un margin, et les margin ne sont pas comptés dans la height d'un element
                // 32 -> on enleve la height du wrapper d'alerte de fundation
            }
        },
    },
};
</script>

<style lang="scss">
.rights-panel {
    .trigger-modal-button-container {
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;
        margin-bottom: 20px !important;
    }
    .group-selector {
        .el-input__inner::placeholder {
            opacity: 1;
            color: #111111 !important;
        }
    }
    .channel-takers{
        overflow-y: scroll;
    }
}
.rights-management-modal-title {
    color: #BBBBBB;
    text-transform: none;
    margin-bottom: 20px !important;
}
.modal-action-button-container {
    display: flex;
    justify-content: flex-end;
    .validate-btn {
        border-radius: 2px !important;
        padding: 5px 5px !important;
        height: 25px !important;
        margin: auto 0 auto 10px;
        font-size: 12px;
        line-height: 12px;
        background-color: #00a3ff;
        color: white !important;
        border: none !important;
        &:hover {
            background-color: #66b1ff !important;
        }
        &.is-disabled {
            background-color: #333333 !important;
            color: #222222 !important;
            &:hover {
                background-color: #333333 !important;
                color: #222222 !important;
            }
        }
    }
}
.taker-groups-dropdown-select {
    background-color: #444444;
    border-color: #444444;
    .el-select-dropdown__item {
        color: #BBBBBB !important;
    }
    .el-select-dropdown__item.hover, .el-select-dropdown__item:hover {
        background-color: #222222 !important;
    }
    .popper__arrow {
        border-bottom-color: #444444 !important;
        &::after {
            border-bottom-color: #444444 !important;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/page/Channels/channels-rights.vue